/**
 * Gallery ảnh trang chi tiết BĐS — layout hero + lưới 4 ảnh phụ.
 *
 * Mở ImageLightbox khi click; fallback ảnh Unsplash nếu không có dữ liệu.
 */
import { useMemo, useState } from 'react';
import { ImageLightbox } from '../Common/ImageLightbox';
import { TEXT } from '../../shared/languages';

const fallbackImages = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=900&q=80',
];

const GridIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M1.5 3A1.5 1.5 0 0 1 3 1.5h2A1.5 1.5 0 0 1 6.5 3v2A1.5 1.5 0 0 1 5 6.5H3A1.5 1.5 0 0 1 1.5 5V3zm0 6A1.5 1.5 0 0 1 3 7.5h2A1.5 1.5 0 0 1 6.5 9v2A1.5 1.5 0 0 1 5 12.5H3A1.5 1.5 0 0 1 1.5 11V9zm6-6A1.5 1.5 0 0 1 9 1.5h2A1.5 1.5 0 0 1 12.5 3v2A1.5 1.5 0 0 1 11 6.5H9A1.5 1.5 0 0 1 7.5 5V3zm0 6A1.5 1.5 0 0 1 9 7.5h2A1.5 1.5 0 0 1 12.5 9v2A1.5 1.5 0 0 1 11 12.5H9A1.5 1.5 0 0 1 7.5 11V9z" />
  </svg>
);

/**
 * Gallery ảnh chi tiết BĐS.
 * @param {Object} props
 * @param {Array} [props.images] - Mảng ảnh từ API (url, is_cover)
 */
export const PropertyGallery = ({ images = [] }) => {
  const t = TEXT.property.detail;
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const galleryUrls = useMemo(() => {
    if (!images.length) {
      return fallbackImages;
    }

    return [...images]
      .sort((firstImage, secondImage) => Number(secondImage.is_cover) - Number(firstImage.is_cover))
      .map((image) => image.url);
  }, [images]);

  const displayUrls = useMemo(() => {
    const source = galleryUrls.length ? galleryUrls : fallbackImages;
    const padded = [...source];

    while (padded.length < 5) {
      padded.push(source[padded.length % source.length]);
    }

    return padded.slice(0, 5);
  }, [galleryUrls]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const extraPhotoCount = Math.max(galleryUrls.length - 5, 0);

  return (
    <>
      <div className="property-gallery">
        <button
          className="property-gallery-trigger property-gallery-trigger--hero"
          type="button"
          onClick={() => openLightbox(0)}
          aria-label={t.openGallery}
        >
          <img src={displayUrls[0]} alt={t.galleryMainAlt} />
        </button>

        <div className="property-gallery-grid">
          {displayUrls.slice(1, 5).map((imageUrl, index) => {
            const galleryIndex = index + 1;
            const isMoreCell = index === 3 && extraPhotoCount > 0;

            return (
              <button
                key={`${imageUrl}-${galleryIndex}`}
                className="property-gallery-trigger"
                type="button"
                onClick={() => openLightbox(isMoreCell ? 4 : galleryIndex)}
                aria-label={t.openGallery}
              >
                <img src={imageUrl} alt={t.gallerySideAlt} />
                {isMoreCell && (
                  <span className="property-gallery-more">
                    +{extraPhotoCount} {t.morePhotos}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="property-gallery-show-all"
          onClick={() => openLightbox(0)}
        >
          <GridIcon />
          {t.showAllPhotos}
        </button>
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={galleryUrls.length ? galleryUrls : fallbackImages}
          initialIndex={lightboxIndex}
          alt={t.galleryMainAlt}
          onClose={closeLightbox}
        />
      )}
    </>
  );
};
